import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit, inject, signal, ChangeDetectionStrategy } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { AuthService } from '../../core/auth/auth';
import { CompetitionsService } from '../../core/competitions/competitions';
import { PasswordField, PasswordInput } from '../../shared/password-field/password-field';

/** Matches the /join/:code returnUrl authGuard builds (app.routes.ts) — used
 *  to pull the invite code back out so the login page can preview it. */
const JOIN_RETURN_URL_PATTERN = /^\/join\/([A-Za-z0-9]+)$/;

@Component({
  selector: 'app-login',
  imports: [ReactiveFormsModule, RouterLink, PasswordField, PasswordInput],
  templateUrl: './login.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './login.css',
})
export class Login implements OnInit {
  private readonly auth = inject(AuthService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly competitions = inject(CompetitionsService);

  protected readonly submitting = signal(false);
  protected readonly errorMessage = signal<string | null>(null);
  /** True after a login rejected for an unverified email — shows the
   *  "Resend verification email" action (the original link expires after
   *  24 hours and logging in never re-sends it). */
  protected readonly needsVerification = signal(false);
  protected readonly resending = signal(false);
  protected readonly resendMessage = signal<string | null>(null);
  /** Set only when returnUrl points at a valid invite (F-004) — null for a
   *  direct visit to /login, an unrelated returnUrl, or an invalid code. */
  readonly inviteCompetitionName = signal<string | null>(null);

  readonly form = new FormGroup({
    email: new FormControl('', {
      nonNullable: true,
      validators: [Validators.required, Validators.email],
    }),
    password: new FormControl('', {
      nonNullable: true,
      validators: [Validators.required],
    }),
  });

  async ngOnInit(): Promise<void> {
    const match = this.route.snapshot.queryParamMap
      .get('returnUrl')
      ?.match(JOIN_RETURN_URL_PATTERN);
    if (!match) return;

    try {
      const { name } = await this.competitions.previewInvite(match[1]);
      this.inviteCompetitionName.set(name);
    } catch {
      // Invalid/expired code — fall back to the plain login page rather
      // than surfacing an error for something the user didn't initiate.
    }
  }

  async submit(): Promise<void> {
    if (this.form.invalid || this.submitting()) {
      this.form.markAllAsTouched();
      return;
    }

    this.submitting.set(true);
    this.errorMessage.set(null);
    this.needsVerification.set(false);
    this.resendMessage.set(null);
    try {
      await this.auth.login(this.form.getRawValue());
      await this.router.navigateByUrl(this.postLoginUrl());
    } catch (err) {
      this.needsVerification.set(
        err instanceof HttpErrorResponse && err.status === 403,
      );
      this.errorMessage.set(this.messageFor(err));
    } finally {
      this.submitting.set(false);
    }
  }

  async resendVerification(): Promise<void> {
    const email = this.form.controls.email;
    if (email.invalid || this.resending()) return;

    this.resending.set(true);
    this.resendMessage.set(null);
    try {
      await this.auth.resendVerification(email.value);
      this.resendMessage.set(
        'If that account still needs verifying, we’ve sent a new link — check your inbox.',
      );
    } catch (err) {
      this.resendMessage.set(
        err instanceof HttpErrorResponse && err.status === 429
          ? 'Too many attempts — please wait a minute and try again.'
          : 'Something went wrong on our end — give it another try in a moment.',
      );
    } finally {
      this.resending.set(false);
    }
  }

  /** Sends the user back where authGuard caught them (e.g. /join/:code), falling
   *  back to My Competitions — the only thing a freshly-logged-in user can
   *  actually do here. Only accepts a same-app relative path — never an
   *  absolute/external URL, even though navigateByUrl can't leave the app anyway. */
  private postLoginUrl(): string {
    const returnUrl = this.route.snapshot.queryParamMap.get('returnUrl');
    return returnUrl && returnUrl.startsWith('/') ? returnUrl : '/competitions';
  }

  private messageFor(err: unknown): string {
    if (err instanceof HttpErrorResponse) {
      if (err.status === 403) {
        return 'Please verify your email before logging in. The link we sent when you signed up expires after 24 hours — you can send a new one below.';
      }
      if (err.status === 401) {
        return 'That email and password combination doesn’t match.';
      }
      if (err.status === 429) {
        return 'Too many attempts — please wait a minute and try again.';
      }
    }
    return 'Something went wrong on our end — give it another try in a moment.';
  }
}
